import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { categories } from "@/mocks/categories";
import { articles } from "@/mocks/articles";
import type { Article } from "@/mocks/types";

const logoSmall = "灼";
const brandName = "灼见社";
const brandSub = "独家内幕 · 深度揭秘";

interface SearchHit {
  article: Article;
}

export default function SiteHeader() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const location = useLocation();
  const searchBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setMobileOpen(false);
    setSearchOpen(false);
    setQuery("");
  }, [location.pathname]);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (searchBoxRef.current && !searchBoxRef.current.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const navItems = [
    { label: "首页", path: "/", active: location.pathname === "/" },
    ...categories.map((c) => ({
      label: c.short,
      path: `/channel/${c.slug}`,
      active: location.pathname === `/channel/${c.slug}`,
    })),
    { label: "关于", path: "/about", active: location.pathname === "/about" },
  ];

  const results: SearchHit[] = (() => {
    if (!query.trim()) return [];
    const kw = query.trim().toLowerCase();
    return articles
      .filter(
        (a) =>
          a.title.toLowerCase().includes(kw) ||
          a.deck.toLowerCase().includes(kw) ||
          a.tags.some((t) => t.toLowerCase().includes(kw)) ||
          a.categoryName.toLowerCase().includes(kw)
      )
      .slice(0, 6)
      .map((article) => ({ article }));
  })();

  return (
    <header className="sticky top-0 z-40 w-full border-b border-background-200/70 bg-background-50/95 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 md:px-6">
        <Link
          to="/"
          aria-label="灼见社首页"
          className="flex shrink-0 items-center gap-2.5"
        >
          <span className="flex h-10 w-10 items-center justify-center rounded-md bg-primary-500 text-lg font-bold text-background-50">
            {logoSmall}
          </span>
          <span className="flex flex-col leading-none">
            <span className="font-heading text-lg font-bold text-foreground-950">
              {brandName}
            </span>
            <span className="mt-1 text-[11px] tracking-wide text-foreground-600">
              {brandSub}
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="主导航">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`whitespace-nowrap rounded-md px-3 py-2 text-sm transition-colors ${
                item.active
                  ? "bg-secondary-100 font-medium text-foreground-950"
                  : "text-foreground-700 hover:bg-background-100 hover:text-primary-600"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-1.5">
          <div className="relative" ref={searchBoxRef}>
            <button
              type="button"
              onClick={() => setSearchOpen((v) => !v)}
              aria-label="站内搜索"
              className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-700 transition-colors hover:bg-background-100 hover:text-primary-600"
            >
              <i className="ri-search-line text-[18px]"></i>
            </button>
            {searchOpen && (
              <div className="absolute right-0 top-11 w-80 rounded-lg border border-background-200 bg-background-50 p-3 shadow-sm">
                <div className="flex items-center gap-2 rounded-md border border-background-200 bg-background-50 px-3">
                  <i className="ri-search-line text-foreground-500"></i>
                  <input
                    type="search"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="搜索标题、栏目或标签…"
                    className="h-10 w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
                    aria-label="搜索关键词"
                  />
                </div>
                <div className="mt-2 max-h-80 overflow-auto">
                  {query.trim() && results.length === 0 && (
                    <p className="px-2 py-3 text-sm text-foreground-600">
                      没有找到相关文章，换个关键词试试。
                    </p>
                  )}
                  {results.map((r) => (
                    <Link
                      key={r.article.id}
                      to={`/article/${r.article.id}`}
                      className="flex gap-2 rounded-md px-2 py-2 hover:bg-background-100"
                    >
                      <span className="mt-0.5 shrink-0 rounded bg-secondary-100 px-1.5 py-0.5 text-[10px] text-secondary-900">
                        {r.article.categoryName}
                      </span>
                      <span className="line-clamp-2 text-sm text-foreground-800">
                        {r.article.title}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/#subscribe"
            className="hidden whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:inline-flex"
          >
            订阅内幕
          </Link>

          <button
            type="button"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={mobileOpen ? "关闭菜单" : "打开菜单"}
            aria-expanded={mobileOpen}
            className="flex h-9 w-9 items-center justify-center rounded-md text-foreground-800 hover:bg-background-100 lg:hidden"
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-2-line"} text-[20px]`}></i>
          </button>
        </div>
      </div>

      {mobileOpen && (
        <nav
          className="border-t border-background-200/70 bg-background-50 lg:hidden"
          aria-label="移动端导航"
        >
          <div className="mx-auto max-w-7xl px-4 py-3">
            <div className="grid grid-cols-2 gap-1">
              {navItems.map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`rounded-md px-3 py-2.5 text-sm ${
                    item.active
                      ? "bg-secondary-100 font-medium text-foreground-950"
                      : "text-foreground-700 hover:bg-background-100"
                  }`}
                >
                  {item.label}
                </Link>
              ))}
            </div>
          </div>
        </nav>
      )}
    </header>
  );
}